import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaArrowLeft, FaGift, FaMoneyBillWave, FaClock, FaUser, FaSyncAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useFireAuth } from '../FireAuthContext';
import useBank from './useBank';
import { db } from '../App';
import { collection, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import DelayRender from './DelayRender';
import './Raffle.css';

const Raffle = () => {
  const [totalTickets, setTotalTickets] = useState(0);
  const [userTickets, setUserTickets] = useState(0);
  const [raffleEndTime, setRaffleEndTime] = useState(new Date());
  const [prizeItems, setPrizeItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [winner, setWinner] = useState(null);
  const [lastWinner, setLastWinner] = useState(null);
  const [lastPrizeMoney, setLastPrizeMoney] = useState(null);
  const [message, setMessage] = useState('');
  const { user } = useFireAuth();
  const { depositMoney } = useBank();
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const fetchRaffleData = useCallback(async () => {
    if (user) {
      try {
        const raffleRef = collection(db, 'raffle');
        const raffleSnapshot = await getDocs(raffleRef);
        let userTicketCount = 0;
        let totalTicketCount = 0;

        raffleSnapshot.forEach(doc => {
          const data = doc.data();
          totalTicketCount += 1;
          if (data.userId === user.uid) {
            userTicketCount += 1;
          }
        });

        setTotalTickets(totalTicketCount);
        setUserTickets(userTicketCount);

        const raffleDocRef = doc(db, 'counters', 'raffle');
        const raffleDoc = await getDoc(raffleDocRef);
        if (raffleDoc.exists()) {
          const prizeItemIds = raffleDoc.data().prize;
          const prizeItemsData = await Promise.all(prizeItemIds.map(async (prizeItemId) => {
            const prizeItemDocRef = doc(db, 'items', prizeItemId);
            const prizeItemDoc = await getDoc(prizeItemDocRef);
            return prizeItemDoc.exists() ? { id: prizeItemDoc.id, ...prizeItemDoc.data() } : null;
          }));
          setPrizeItems(prizeItemsData.filter(item => item !== null));

          const winnerName = raffleDoc.data().winner;
          const lastPrize = raffleDoc.data().lastPrizeMoney;

          if (winnerName) {
            setLastWinner(winnerName);
          }
          setWinner(winnerName || null);

          if (lastPrize) {
            setLastPrizeMoney(lastPrize);
          }

          const raffleEndDate = raffleDoc.data().timestamp.toDate();
          setRaffleEndTime(raffleEndDate);
        }
      } catch (error) {
        console.error('Error fetching raffle data:', error);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchRaffleData();
  }, [user, fetchRaffleData]);

  const handleBuyTicket = async () => {
    if (!user || isProcessing) return;
    if (user.money < 10) {
      setMessage('Insufficient funds. Tickets cost 💴10.');
      return;
    }
    setIsProcessing(true);
    setMessage('');
    try {
      await depositMoney(10, false);
      await addDoc(collection(db, 'raffle'), {
        userId: user.uid,
        timestamp: new Date()
      });
      setUserTickets(prevCount => prevCount + 1);
      setTotalTickets(prevCount => prevCount + 1);
      setMessage('Ticket purchased successfully!');
    } catch (error) {
      console.error('Error buying ticket:', error);
      setMessage('Failed to purchase ticket. Please try again.');
    } finally {
      setTimeout(() => setIsProcessing(false), 750);
    }
  };

  const formatCountdown = (timeLeft) => {
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  const formatPrizeDate = (date) => {
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('en-GB', options);
  };

  const formatPrizeItems = (items) => {
    const itemMap = {};
    items.forEach(item => {
      if (itemMap[item.name]) {
        itemMap[item.name].count += 1;
        itemMap[item.name].totalValue += item.price;
      } else {
        itemMap[item.name] = { ...item, count: 1, totalValue: item.price };
      }
    });
    return Object.values(itemMap);
  };

  const formattedPrizeItems = formatPrizeItems(prizeItems);

  useEffect(() => {
    const updateCountdown = () => {
      const timeLeft = raffleEndTime.getTime() - new Date().getTime();
      if (timeLeft <= 0) {
        clearInterval(intervalRef.current);
        fetchRaffleData();
      }
    };

    intervalRef.current = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalRef.current);
  }, [raffleEndTime, fetchRaffleData]);

  const handleRefresh = () => {
    fetchRaffleData();
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  const prizeMoney = totalTickets * 9;
  const timeLeft = raffleEndTime.getTime() - new Date().getTime();
  const isTicketSalesClosed = timeLeft <= 2 * 60 * 1000;

  return (
    <div className="raffle-section">
      <div className="raffle-container">
        <div className="raffle-header">
          <FaArrowLeft className="raffle-back-arrow" onClick={() => navigate(-1)} />
          <h2 className="raffle-title">Weekly Raffle</h2>
        </div>
        
        <div className="raffle-grid">
          <div className="raffle-card countdown">
            <h3><FaClock /> Prize Draw</h3>
            <p>{formatPrizeDate(raffleEndTime)}</p>
            {timeLeft > 0 ? (
              <p className="countdown-timer">
                {formatCountdown(timeLeft)}
                <FaSyncAlt className="refresh-icon" onClick={handleRefresh} style={{ cursor: 'pointer', marginLeft: '10px' }} />
              </p>
            ) : (
              <p className="countdown-timer">{winner ? `Winner: ${winner}` : 'Drawing...'}</p>
            )}
          </div>

          <div className="raffle-card prize">
            <h3><FaGift /> Prize</h3>
            <p>Prize Money: 💴 {prizeMoney}</p>
            <p>Items:</p>
            <div className="prize-items">
              {formattedPrizeItems.map(prizeItem => (
                <div key={prizeItem.id} className="prize-item">
                  <img src={prizeItem.imageUrl} alt={prizeItem.name} className="prize-item-image" />
                  <div className="prize-item-details">
                    <p>{prizeItem.name} {prizeItem.count > 1 && `(${prizeItem.count}x)`}</p>
                    <p>{prizeItem.currency === 'hahumCoin' ? '🪙' : '💴'} {prizeItem.totalValue}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="raffle-card user-info">
            <h3><FaUser /> Your Info</h3>
            <p>Tickets: {userTickets}</p>
            <p>Balance: 💴{user.money}</p>
          </div>

          <div className="raffle-card buy-tickets">
            <h3><FaMoneyBillWave /> Buy Tickets</h3>
            <button className="btn-action" onClick={handleBuyTicket} disabled={isProcessing || isTicketSalesClosed} style={isTicketSalesClosed ? { cursor: 'not-allowed' } : {}}>
              {isProcessing ? 'Processing...' : 'Buy Ticket (💴10)'}
            </button>
            {isTicketSalesClosed && <p className="ticket-sales-closed">Ticket sales closed.</p>}
            {message && <p className="raffle-message">{message}</p>}
          </div>
        </div>

        {lastWinner && (
          <div className="winner-announcement">
            <p>Last Winner: {lastWinner}</p>
            {lastPrizeMoney && <p>Last Prize Money: 💴 {lastPrizeMoney}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default DelayRender(Raffle, 300);
